import { useInView } from "../hooks/useInView";
import { RevealChild } from "./Reveal";
import { FiArrowUpRight } from "react-icons/fi";

const projects = [
  {
    title: "FinTech & Web3 Infrastructure Platform",
    description:
      "Architected and deployed production infrastructure for FinTech and Web3 workloads with Docker, Traefik reverse proxy, and NestJS microservices. Built networking, routing, and observability layers for high-traffic node infrastructure with disaster recovery planning on AWS.",
    tech: ["Docker", "Traefik", "NestJS", "Terraform", "AWS"],
    url: "https://brain.rakeshkadam.me/",
  },
  {
    title: "Internal Observability Stack",
    description:
      "Built a scalable internal observability platform using Teleport for zero-trust access, Loki for log aggregation, and Grafana Alloy for telemetry shipping. Implemented identity-based security and scalable logging patterns across distributed systems.",
    tech: ["Teleport", "Loki", "Grafana Alloy", "Prometheus", "GitOps"],
    url: "https://brain.rakeshkadam.me/",
  },
  {
    title: "Digital Brain - Agent-Maintained Knowledge Base",
    description:
      "Built a public-facing digital brain on Quartz with Cloudflare zero-trust gating and AI-powered ingestion. Follows Andrej Karpathy's LLM-Wiki pattern - an agent-maintained artifact where AI handles structure, cross-references, and synthesis.",
    tech: ["Quartz", "Cloudflare", "AI Ingest", "Claude Code", "Markdown"],
    url: "https://brain.rakeshkadam.me/",
  },
  {
    title: "Side Projects - Vibe-Coded Tools",
    description:
      "A collection of self-hosted CLI, drawing, todo, and game applications. Vibe-coded with modern TypeScript and deployed on personal infrastructure. Focus on practical utility and rapid iteration.",
    tech: ["TypeScript", "Node.js", "Bash", "Docker", "Python"],
    url: "https://brain.rakeshkadam.me/",
  },
];

const FeaturedProjects = () => {
  const { ref, inView } = useInView();

  return (
    <section
      id="projects-cta"
      className="py-12 text-light-text dark:text-text-primary"
      ref={ref}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <RevealChild visible={inView} delay={0}>
          <div className="mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">
              Featured Projects
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight max-w-2xl tracking-tight">
              Built in production.{" "}
              <span className="text-accent">Synthesized in the Brain.</span>
            </h2>
            <p className="text-light-muted dark:text-text-muted text-base md:text-lg leading-relaxed max-w-2xl">
              Every project is real infrastructure: FinTech & Web3 platforms, observability
              stacks, AI-powered knowledge systems, and self-hosted tools.
            </p>
          </div>
        </RevealChild>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {projects.map((project, i) => (
            <RevealChild
              key={project.title}
              visible={inView}
              delay={100 + i * 80}
            >
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block h-full rounded-xl border border-light-border dark:border-border-subtle bg-light-surface dark:bg-surface-1 p-7 transition-all duration-300 hover:border-accent/30 hover:shadow-card-hover dark:hover:shadow-glow"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold group-hover:text-accent transition-colors">
                    {project.title}
                  </h3>
                  <FiArrowUpRight
                    size={16}
                    className="mt-1 text-light-muted dark:text-text-muted opacity-0 group-hover:opacity-100 transition-all group-hover:text-accent"
                  />
                </div>
                <p className="text-light-muted dark:text-text-muted text-sm leading-relaxed mb-6">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-light-surface-2 dark:bg-surface-2 text-light-muted dark:text-text-muted border border-light-border dark:border-border-subtle"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </a>
            </RevealChild>
          ))}
        </div>

        <RevealChild visible={inView} delay={500}>
          <div className="text-center mt-12">
            <a
              href="https://brain.rakeshkadam.me"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-accent font-semibold text-sm hover:text-accent-muted transition-colors"
            >
              Explore the Brain
              <FiArrowUpRight
                size={14}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </RevealChild>
      </div>
    </section>
  );
};

export default FeaturedProjects;
